import { H5, TertiaryButton, Body2, Card } from "@coherehealth/common";
import { useSearchElasticStats, Queue } from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";
import { useState, useEffect, Dispatch, SetStateAction, useMemo } from "react";
import { DateFilterOptions, ChartData } from "../util/ChartUtil";
import {
  caseChartDataMapper,
  dueDatesChartQueueBasedQueryGenerator,
  getQueueFilterClausesWithoutFaxAutoAttach,
} from "../util/QueryUtil";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import config from "api/config";
import DueDatesOverTimeChart from "../util/charts/DueDatesOverTimeChart";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chartCard: {
      height: 278,
      margin: "32px 0px",
      padding: "16px 24px",
    },
    chartContainer: {
      height: 202,
      margin: "18px -30px 0px -16px",
    },
    flexSpaceBetween: {
      display: "flex",
      justifyContent: "space-between",
    },
  })
);

interface DueDatePageContentProps {
  queues: Queue[] | null;
  dateValue: DateFilterOptions;
}

const DueDateChartsContent = ({ dateValue, queues }: DueDatePageContentProps) => {
  const [hasError, setHasError] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (hasError) {
      enqueueSnackbar(`Failed to load data`, { variant: "error", preventDuplicate: true });
    }
  }, [enqueueSnackbar, hasError]);

  return (
    <div>
      {queues?.map((queue) => (
        <QueueBasedChartCard key={queue.id} dateValue={dateValue} setHasError={setHasError} queue={queue} />
      ))}
    </div>
  );
};

interface QueueBasedChartCardProps {
  queue: Queue;
  setHasError: Dispatch<SetStateAction<boolean>>;
  dateValue: DateFilterOptions;
}

const QueueBasedChartCard = ({ dateValue, setHasError, queue }: QueueBasedChartCardProps) => {
  const classes = useStyles();

  const [data, setData] = useState<ChartData>([]);
  const { filters, filterTree, useFilterTree } = queue;
  const queueBasedSearchPayload = useMemo(
    () =>
      dueDatesChartQueueBasedQueryGenerator(
        dateValue,
        getQueueFilterClausesWithoutFaxAutoAttach({ filters, filterTree, useFilterTree })
      ),
    [dateValue, filters, filterTree, useFilterTree]
  );

  const { mutate: searchElasticStats, error } = useSearchElasticStats({
    base: `${config.QM_SERVICE_API_URL}`,
  });

  useEffect(() => {
    const fetchStatsData = async () => {
      const res = await searchElasticStats(queueBasedSearchPayload);
      const aggregatedData = caseChartDataMapper(res.aggregations?.dueDates, dateValue);
      setData(aggregatedData);
    };

    fetchStatsData();
  }, [searchElasticStats, queueBasedSearchPayload, dateValue]);

  useEffect(() => {
    if (error) {
      setHasError(true);
    }
  }, [error, setHasError]);

  return (
    <Card className={classes.chartCard}>
      <div className={classes.flexSpaceBetween}>
        <H5>{queue.name}</H5>
        <TertiaryButton style={{ height: 32, padding: "16px 0px", marginTop: -8 }} disabled>
          <div style={{ display: "flex", alignItems: "center" }}>
            <OpenInNewIcon style={{ marginRight: 11 }} />
            <Body2>View in list</Body2>
          </div>
        </TertiaryButton>
      </div>
      <div className={classes.chartContainer}>
        <DueDatesOverTimeChart data={data} yLabel="Cases count" />
      </div>
    </Card>
  );
};

export default DueDateChartsContent;
